import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, getAuthToken } from "@/App";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Truck, Eye, Edit, MoreHorizontal, Download, FileText, RefreshCw, X, Search,
} from "lucide-react";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { useTableSort, SortableHeader } from "@/lib/tableSort";
import SellerMultiSelect from "@/components/SellerMultiSelect";
import DispatchEditModal from "@/components/DispatchEditModal";
import DispatchAnalytics from "@/components/DispatchAnalytics";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toDMY } from "@/lib/dateOnly";

const STATUS_PILL = {
  to_be_dispatch: { label: "To be Dispatch", bg: "#FFF4E0", color: "#8A5A00", border: "#F1DFB5" },
  dispatched:     { label: "Dispatched",     bg: "#E1F5EE", color: "#085041", border: "#C0E4D5" },
};

const TYPE_PILL = {
  online:  { label: "Online",  bg: "#E6F1FB", color: "#0C447C", border: "#C7DEF3" },
  offline: { label: "Offline", bg: "#F1EFE8", color: "#444441", border: "#E0DDD3" },
};

// On-Time Dispatch and Pre-Pull are "green"; every other (delay) reason is amber.
const SUB_STATUS_GREEN = new Set(["On-Time Dispatch", "Pre-Pull"]);
const subStatusStyle = (s) =>
  SUB_STATUS_GREEN.has(s)
    ? { bg: "#E1F5EE", color: "#085041", border: "#C0E4D5" }
    : { bg: "#FFF4E0", color: "#8A5A00", border: "#F1DFB5" };

const formatPickup = (iso) => toDMY(iso);

// Today's calendar date as YYYY-MM-DD (local time), used as the default date filter.
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function DispatchPanel() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters — Date filter defaults to today and always filters on Final Pickup Date.
  // State initializes fresh on every mount, so a page refresh/reopen resets to today.
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [statusFilter, setStatusFilter] = useState("all");
  const [sellerFilter, setSellerFilter] = useState([]);

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editingDispatchId, setEditingDispatchId] = useState(null);
  const [bulkDownloading, setBulkDownloading] = useState(false);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dispatchRes, sellersRes] = await Promise.all([
        axios.get(`${API}/dispatch`, { headers: { Authorization: `Bearer ${getAuthToken()}` } }),
        axios.get(`${API}/sellers`, { headers: { Authorization: `Bearer ${getAuthToken()}` } }),
      ]);
      setRows(dispatchRes.data || []);
      setSellers(sellersRes.data || []);
    } catch {
      toast.error("Failed to load dispatch panel");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/dispatch`, { headers: { Authorization: `Bearer ${getAuthToken()}` } });
      setRows(res.data || []);
    } catch {
      // silent
    } finally {
      setRefreshing(false);
    }
  };

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      // Search (quotation_id, seller_name)
      const s = searchQuery.trim().toLowerCase();
      if (s) {
        const hay = `${r.quotation_id} ${r.seller_name || ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      // Date range on Final Pickup Date only (empty range = All Time).
      if (dateFrom || dateTo) {
        const fp = r.final_pickup_date ? String(r.final_pickup_date).substring(0, 10) : null;
        if (!fp) return false;
        if (dateFrom && fp < dateFrom) return false;
        if (dateTo && fp > dateTo) return false;
      }
      // Status
      if (statusFilter !== "all" && (r.dispatch_status || "to_be_dispatch") !== statusFilter) return false;
      // Seller
      if (sellerFilter.length > 0 && !sellerFilter.includes(r.seller_id)) return false;
      return true;
    });
  }, [rows, searchQuery, dateFrom, dateTo, statusFilter, sellerFilter]);

  // Default sort: rows with dispatch_status='to_be_dispatch' float to the top, then by most-recent
  // order_date. Users can still override by clicking any column header — third click on the same
  // header resets back to this default (handled by useTableSort).
  const { sortedRows, sort, toggleSort } = useTableSort(filtered, {
    defaultKey: "_status_priority",
    defaultOrder: "asc",
    accessors: {
      // Composite priority: 0 for to_be_dispatch (top), 1 for dispatched.
      // Within the same status group we tie-break by most recent order_date by subtracting the
      // timestamp from a large constant, so newer rows sort first under ascending order.
      _status_priority: (r) => {
        const status = r.dispatch_status || "to_be_dispatch";
        const statusRank = status === "to_be_dispatch" ? 0 : 1;
        const ts = r.order_date ? new Date(r.order_date).getTime() || 0 : 0;
        // 1e16 dwarfs any reasonable ms timestamp; subtraction inverts the order for desc-tiebreak.
        return statusRank * 1e16 + (1e15 - ts);
      },
      quotation_id: (r) => r.quotation_id,
      order_date: (r) => r.order_date,
      seller_name: (r) => r.seller_name || "",
      quotation_type: (r) => r.quotation_type || "offline",
      quantity_planned: (r) => Number(r.quantity_planned) || 0,
      quantity_sent: (r) => (r.quantity_sent == null ? null : Number(r.quantity_sent)),
      total_invoice_value: (r) => Number(r.total_invoice_value) || 0,
      dispatch_status: (r) => r.dispatch_status || "to_be_dispatch",
      transporter_name: (r) => r.transporter_name || "",
      dispatch_sub_status: (r) => r.dispatch_sub_status || "",
      pickup_date: (r) => r.pickup_date || null,
      rescheduled_pickup_date: (r) => r.rescheduled_pickup_date || null,
      final_pickup_date: (r) => r.final_pickup_date || null,
    },
  });

  // "Today" is the default, so it doesn't count as an active filter (Clear resets to today).
  const isDefaultDate = dateFrom === todayISO() && dateTo === todayISO();
  const hasActiveFilters = searchQuery || !isDefaultDate || statusFilter !== "all" || sellerFilter.length > 0;

  const clearFilters = () => {
    setSearchQuery("");
    setDateFrom(todayISO());
    setDateTo(todayISO());
    setStatusFilter("all");
    setSellerFilter([]);
  };

  const openEdit = (row) => {
    setEditingDispatchId(row.id);
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditingDispatchId(null);
  };

  const handleModalSaved = () => {
    fetchAll();
  };

  const downloadFileByPath = async (storagePath, fallbackName) => {
    if (!storagePath) {
      toast.error("File not available");
      return;
    }
    try {
      const res = await axios.get(`${API}/files/${storagePath}`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fallbackName || storagePath.split("/").pop() || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  // Bulk-download all Invoice + E-Way Bill PDFs for the selected Final Pickup Date range as one ZIP.
  const downloadDispatchDocs = async () => {
    setBulkDownloading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API}/dispatch/bulk-download`, {
        params,
        responseType: "blob",
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      // Read the summary header before touching the blob.
      let summary = null;
      try { summary = JSON.parse(res.headers["x-bulk-summary"] || "null"); } catch { /* ignore */ }

      if (summary && summary.invoices_downloaded === 0 && summary.eway_bills_downloaded === 0) {
        toast.error("No documents found for the selected Final Pickup Date.");
        return;
      }

      const blob = new Blob([res.data], { type: "application/zip" });
      const label = dateFrom && dateFrom === dateTo ? dateFrom
        : (dateFrom || dateTo ? `${dateFrom || "start"}_to_${dateTo || "end"}` : "AllDates");
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Dispatch_${label}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      if (summary) {
        toast.success(
          `Downloaded — ${summary.invoices_downloaded} Invoice${summary.invoices_downloaded === 1 ? "" : "s"}, ` +
          `${summary.eway_bills_downloaded} E-Way Bill${summary.eway_bills_downloaded === 1 ? "" : "s"} ` +
          `(from ${summary.total_quotations} quotation${summary.total_quotations === 1 ? "" : "s"}` +
          `${summary.missing_documents ? `, ${summary.missing_documents} missing documents` : ""})`,
          { duration: 6000 }
        );
      } else {
        toast.success("Dispatch documents downloaded");
      }
    } catch {
      toast.error("Bulk download failed");
    } finally {
      setBulkDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Truck className="h-5 w-5 text-[#32B4AC]" />
          <h1 className="text-xl font-semibold text-[#0F0F0F]" data-testid="dispatch-panel-title">Dispatch Panel</h1>
          <span className="text-xs text-muted-foreground">({sortedRows.length} of {rows.length})</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            onClick={downloadDispatchDocs}
            disabled={bulkDownloading}
            className="bg-[#32B4AC] hover:bg-[#279a93] text-white"
            data-testid="dispatch-bulk-download-btn"
          >
            <Download className={`h-3.5 w-3.5 mr-1.5 ${bulkDownloading ? "animate-pulse" : ""}`} />
            {bulkDownloading ? "Preparing…" : "Download Dispatch Documents"}
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} data-testid="dispatch-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <Tabs defaultValue="list" className="w-full">
        <TabsList data-testid="dispatch-tabs">
          <TabsTrigger value="list" data-testid="dispatch-tab-list">Dispatch List</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="dispatch-tab-analytics">Dispatch Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-4">
      <Card>
        <CardContent className="pt-4">
          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap mb-4">
            <div className="relative flex-1 min-w-[220px] max-w-[320px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search by Quotation ID or Seller..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 pl-8 text-xs"
                data-testid="dispatch-search"
              />
            </div>
            <DateRangeFilter from={dateFrom} to={dateTo} onChange={(f,t)=>{ setDateFrom(f); setDateTo(t); }} testId="dispatch-date-range" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[170px] h-9 text-xs" data-testid="dispatch-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="dispatch-status-filter-all">All Statuses</SelectItem>
                <SelectItem value="to_be_dispatch" data-testid="dispatch-status-filter-to-be-dispatch">To be Dispatch</SelectItem>
                <SelectItem value="dispatched" data-testid="dispatch-status-filter-dispatched">Dispatched</SelectItem>
              </SelectContent>
            </Select>
            <SellerMultiSelect
              sellers={sellers}
              selectedIds={sellerFilter}
              onChange={setSellerFilter}
              placeholder="All Sellers"
            />
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 px-2 text-xs" data-testid="dispatch-clear-filters">
                <X className="h-3 w-3 mr-1" />
                Clear
              </Button>
            )}
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-auto max-h-[640px]">
              <Table containerClassName="overflow-visible">
                <TableHeader className="sticky top-0 z-20">
                  <TableRow className="bg-slate-100 hover:bg-slate-100">
                    <SortableHeader sortKey="quotation_id" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Quotation ID</SortableHeader>
                    <SortableHeader sortKey="seller_name" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Seller Name</SortableHeader>
                    <SortableHeader sortKey="quotation_type" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Quotation Type</SortableHeader>
                    <SortableHeader sortKey="quantity_planned" sort={sort} onToggle={toggleSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Qty Planned</SortableHeader>
                    <SortableHeader sortKey="quantity_sent" sort={sort} onToggle={toggleSort} align="right" className="bg-slate-100 border-b-2 border-slate-200">Qty Sent</SortableHeader>
                    <SortableHeader sortKey="dispatch_status" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Status</SortableHeader>
                    <SortableHeader sortKey="dispatch_sub_status" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Dispatch Sub-Status</SortableHeader>
                    <SortableHeader sortKey="transporter_name" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Transporter</SortableHeader>
                    <SortableHeader sortKey="pickup_date" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Pickup Date</SortableHeader>
                    <SortableHeader sortKey="rescheduled_pickup_date" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Rescheduled Pickup Date</SortableHeader>
                    <SortableHeader sortKey="final_pickup_date" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Final Pickup Date</SortableHeader>
                    <TableHead className="text-right bg-slate-100 border-b-2 border-slate-200 font-semibold text-slate-700">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} className="text-center py-10 text-muted-foreground text-sm">
                        {rows.length === 0
                          ? "No dispatches yet. Quotations appear here after invoice upload."
                          : "No dispatches match your filters."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedRows.map((r) => {
                      const statusKey = r.dispatch_status || "to_be_dispatch";
                      const statusStyle = STATUS_PILL[statusKey];
                      const typeKey = (r.quotation_type || "offline").toLowerCase();
                      const typeStyle = TYPE_PILL[typeKey] || TYPE_PILL.offline;
                      // Partial dispatch: qty_sent is set, > 0, and strictly less than planned.
                      // Equal → no flag. Null/blank → no flag yet.
                      const planned = Number(r.quantity_planned) || 0;
                      const sent = r.quantity_sent == null ? null : Number(r.quantity_sent);
                      const isPartial = sent != null && sent > 0 && planned > 0 && sent < planned;
                      return (
                        <TableRow key={r.id} className={isPartial ? "bg-red-50/60" : ""} data-testid={isPartial ? `dispatch-row-partial-${r.id}` : `dispatch-row-${r.id}`}>
                          <TableCell className="font-mono font-semibold">{r.quotation_id}</TableCell>
                          <TableCell className="text-xs">{r.seller_name}</TableCell>
                          <TableCell>
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border"
                              style={{ background: typeStyle.bg, color: typeStyle.color, borderColor: typeStyle.border }}
                            >
                              {typeStyle.label}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs text-right font-mono">{planned.toLocaleString("en-IN")}</TableCell>
                          <TableCell className={`text-xs text-right font-mono ${isPartial ? "text-red-700 font-semibold" : ""}`} data-testid={`dispatch-qty-sent-${r.id}`}>
                            {sent == null ? <span className="text-muted-foreground">—</span> : sent.toLocaleString("en-IN")}
                          </TableCell>
                          <TableCell>
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border"
                              style={isPartial
                                ? { background: "#FEE2E2", color: "#991B1B", borderColor: "#FCA5A5" }
                                : { background: statusStyle.bg, color: statusStyle.color, borderColor: statusStyle.border }}
                              title={isPartial ? `Partial: ${sent}/${planned} dispatched` : undefined}
                              data-testid={`dispatch-status-pill-${r.id}`}
                            >
                              {isPartial ? `Partial (${sent}/${planned})` : statusStyle.label}
                            </span>
                          </TableCell>
                          <TableCell data-testid={`dispatch-sub-status-${r.id}`}>
                            {(() => {
                              // Sub-status only shows once Dispatched; blank while "To be Dispatch".
                              if (r.dispatch_status !== "dispatched" || !r.dispatch_sub_status) {
                                return <span className="text-muted-foreground text-xs">—</span>;
                              }
                              const ss = r.dispatch_sub_status;
                              const st = subStatusStyle(ss);
                              return (
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border"
                                  style={{ background: st.bg, color: st.color, borderColor: st.border }}
                                  title={ss === "Others" && r.dispatch_sub_status_reason ? r.dispatch_sub_status_reason : undefined}
                                >
                                  {ss}{ss === "Others" && r.dispatch_sub_status_reason ? ": " + r.dispatch_sub_status_reason : ""}
                                </span>
                              );
                            })()}
                          </TableCell>
                          <TableCell className="text-xs">{r.transporter_name || "—"}</TableCell>
                          <TableCell className="text-xs" data-testid={`dispatch-pickup-date-${r.id}`}>{formatPickup(r.pickup_date)}</TableCell>
                          <TableCell className="text-xs" data-testid={`dispatch-rescheduled-pickup-date-${r.id}`}>{formatPickup(r.rescheduled_pickup_date)}</TableCell>
                          <TableCell className="text-xs font-medium" data-testid={`dispatch-final-pickup-date-${r.id}`}>{formatPickup(r.final_pickup_date)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => navigate(`/quotations/${r.id}`)} title="View" data-testid={`dispatch-view-${r.id}`}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => openEdit(r)} title="Edit Dispatch" data-testid={`dispatch-edit-${r.id}`}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="ghost" data-testid={`dispatch-more-${r.id}`} title="More">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                  <DropdownMenuItem
                                    disabled={!r.invoice_file_url}
                                    onClick={() => downloadFileByPath(r.invoice_file_url, `invoice-${r.quotation_id}.pdf`)}
                                    data-testid={`dispatch-download-invoice-${r.id}`}
                                  >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download Invoice
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    disabled={!r.eway_bill_file_url}
                                    onClick={() => downloadFileByPath(r.eway_bill_file_url, `eway-${r.quotation_id}.pdf`)}
                                    data-testid={`dispatch-download-eway-${r.id}`}
                                  >
                                    <FileText className="h-4 w-4 mr-2" />
                                    Download E-Way Bill
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="analytics" className="mt-4">
          <DispatchAnalytics />
        </TabsContent>
      </Tabs>

      <DispatchEditModal
        open={editOpen}
        dispatchId={editingDispatchId}
        onClose={closeEdit}
        onSaved={handleModalSaved}
      />
    </div>
  );
}
